import { visit } from 'unist-util-visit';

import { isParentNode } from './markdown-plugins-utils.js';

import type { Paragraph, Parents, PhrasingContent, Root, RootContent, Text } from 'mdast';
import type {} from 'remark-math';
import type { Plugin } from 'unified';

/**
 * Renders `[ … ]` expressions, the math convention used before `$$`, as KaTeX.
 *
 * Recovers the expression from already-parsed markdown, so what reaches KaTeX has
 * been through CommonMark inline parsing: backslash escapes are spent (`\_` arrives
 * as `_`), and an expression whose underscores or asterisks paired into emphasis is
 * split across nodes and no longer recognized as math. Expressions containing
 * neither survive intact; the rest cannot be recovered here, which is why `$$` is
 * the supported delimiter for new content.
 */
export const bracketedMathPlugin: Plugin<[], Root> = () => (tree) => {
	expandBracketedMathBlocks(tree);
	expandBracketedInlineMath(tree);
};

const LIKELY_MATH_MARKERS = /\\[A-Za-z]+|[_^]\s*\{|[=<>\u2264\u2265\u2248\u2260]/;

interface BracketedExpression {
	expression: string;
	endIndex: number;
}

function expandBracketedMathBlocks(tree: Root) {
	visit(tree, 'paragraph', (node, index, parent) => {
		if (index === undefined || !isParentNode(parent)) {
			return;
		}

		const expression = parseBracketedMathParagraph(node);
		if (expression === undefined) {
			return;
		}

		parent.children.splice(index, 1, createMathNode(expression));
		return index + 1;
	});
}

function expandBracketedInlineMath(tree: Root) {
	expandBracketedInlineMathChildren(tree, false);
}

function expandBracketedInlineMathChildren(parent: Parents, skipInlineMath: boolean) {
	const shouldSkipChildren = skipInlineMath || shouldSkipInlineMathParent(parent.type);
	for (let index = 0; index < parent.children.length; index += 1) {
		const child = parent.children[index];
		if (child === undefined) {
			continue;
		}
		if (child.type === 'text') {
			if (shouldSkipChildren) {
				continue;
			}
			const replacements = splitBracketedMathTextNode(child);
			if (replacements !== undefined) {
				parent.children.splice(index, 1, ...replacements);
				index += replacements.length - 1;
			}
			continue;
		}

		if (isParentNode(child)) {
			expandBracketedInlineMathChildren(child, shouldSkipChildren);
		}
	}
}

function parseBracketedMathParagraph(node: Paragraph): string | undefined {
	const onlyChild = node.children.length === 1 ? node.children[0] : undefined;
	if (onlyChild?.type !== 'text') {
		return undefined;
	}

	return parseBracketedMathValue(onlyChild.value);
}

function splitBracketedMathTextNode(node: Text): PhrasingContent[] | undefined {
	const replacements: PhrasingContent[] = [];
	let cursor = 0;
	let hasMath = false;

	let searchIndex = 0;
	for (;;) {
		const start = node.value.indexOf('[', searchIndex);
		if (start === -1) {
			break;
		}

		const parsed = readBracketedExpression(node.value, start);
		if (parsed === undefined) {
			searchIndex = start + 1;
			continue;
		}

		const expression = normalizeMathExpression(parsed.expression);
		if (expression === undefined) {
			searchIndex = parsed.endIndex;
			continue;
		}

		if (start > cursor) {
			replacements.push({ type: 'text', value: node.value.slice(cursor, start) });
		}

		replacements.push(createInlineMathNode(expression));
		cursor = parsed.endIndex;
		searchIndex = parsed.endIndex;
		hasMath = true;
	}

	if (!hasMath) {
		return undefined;
	}

	if (cursor < node.value.length) {
		replacements.push({ type: 'text', value: node.value.slice(cursor) });
	}

	return replacements;
}

function parseBracketedMathValue(value: string): string | undefined {
	const trimmed = value.trim();
	if (!trimmed.startsWith('[')) {
		return undefined;
	}

	const parsed = readBracketedExpression(trimmed, 0);
	if (parsed === undefined || parsed.endIndex !== trimmed.length) {
		return undefined;
	}

	return normalizeMathExpression(parsed.expression);
}

function readBracketedExpression(value: string, start: number): BracketedExpression | undefined {
	if (value[start] !== '[') {
		return undefined;
	}

	let depth = 0;
	for (let index = start; index < value.length; index += 1) {
		const char = value[index];
		if (char === '\n') {
			return undefined;
		}
		if (char === '[') {
			depth += 1;
		} else if (char === ']') {
			depth -= 1;
			if (depth === 0) {
				return { expression: value.slice(start + 1, index), endIndex: index + 1 };
			}
		}
	}

	return undefined;
}

function normalizeMathExpression(value: string): string | undefined {
	const expression = value.trim();

	return expression !== '' && LIKELY_MATH_MARKERS.test(expression) ? expression : undefined;
}

function createMathNode(expression: string): RootContent {
	// Match the HAST hints emitted by remark-math so rehype-katex can render this synthetic node.
	return {
		type: 'math',
		value: expression,
		data: {
			hName: 'pre',
			hChildren: [
				{
					type: 'element',
					tagName: 'code',
					properties: { className: ['language-math', 'math-display'] },
					children: [{ type: 'text', value: expression }],
				},
			],
		},
	} as RootContent;
}

function createInlineMathNode(expression: string): PhrasingContent {
	return {
		type: 'inlineMath',
		value: expression,
		data: {
			hName: 'code',
			hProperties: { className: ['language-math', 'math-inline'] },
			hChildren: [{ type: 'text', value: expression }],
		},
	} as PhrasingContent;
}

function shouldSkipInlineMathParent(parentType: string) {
	return parentType === 'link' || parentType === 'linkReference';
}
